/**
 * UploadModal Component
 * Module 07 - Dashboard UI
 * Section 05 - Models Management (Step 5.4)
 *
 * Modal for selecting and enqueueing 3D model files with drag-and-drop support.
 * Uploads continue in the background after the modal closes.
 */

import React, { useState, useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { Upload, X, FileIcon } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { useUploadContext } from '../../contexts/UploadContext';
import { formatBytes } from '../../utils';

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Upload modal component with drag-and-drop file selection
 *
 * @param open - Whether modal is open
 * @param onClose - Callback when modal closes
 *
 * @example
 * <UploadModal
 *   open={showUpload}
 *   onClose={handleClose}
 * />
 */
const UploadModal: React.FC<UploadModalProps> = ({ open, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { enqueueUpload } = useUploadContext();

  // Drag-and-drop configuration
  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    setUploadError(null);

    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        setUploadError('File too large. Maximum size is 50MB.');
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        setUploadError('Invalid file type. Please upload a GLB or GLTF file.');
      } else {
        setUploadError('Invalid file. Please try another.');
      }
      return;
    }

    // Accept first file
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'model/gltf-binary': ['.glb'],
      'model/gltf+json': ['.gltf'],
    },
    maxFiles: 1,
    maxSize: 52428800, // 50MB
  });

  // Handle upload — enqueue and close immediately
  const handleUpload = () => {
    if (!selectedFile) return;
    enqueueUpload(selectedFile);
    handleClose();
  };

  // Handle modal close
  const handleClose = () => {
    setSelectedFile(null);
    setUploadError(null);
    onClose();
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadError(null);
  };

  return (
    <Modal 
      open={open} 
      onClose={handleClose} 
      title="Upload 3D Model" 
      size="medium"
    >
      <div className="space-y-4">
        {/* File selection area (shown when no file selected) */}
        {!selectedFile && (
          <>
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                transition-colors duration-200
                ${isDragActive
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                }
              `}
            >
              <input {...getInputProps()} />
              <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />

              {isDragActive ? (
                <p className="text-primary-600 font-medium">Drop the file here...</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-gray-700 font-medium">
                    Drag and drop a GLB file here
                  </p>
                  <p className="text-sm text-gray-500">
                    or click to browse
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Supported: .glb, .gltf • Max size: 50MB
                  </p>
                </div>
              )}
            </div>

            {/* Error message */}
            {uploadError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{uploadError}</p>
              </div>
            )}
          </>
        )}

        {/* Selected file display (shown when file selected) */}
        {selectedFile && (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <FileIcon className="h-5 w-5 text-primary-600" />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate" title={selectedFile.name}>
                    {selectedFile.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatBytes(selectedFile.size)}
                  </p>
                </div>
              </div>

              <button
                onClick={handleRemoveFile}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                title="Remove file"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!selectedFile}
          >
            Upload
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UploadModal;
